import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CATEGORIES,
  getProjectsByCategory,
  SOCIAL_POSTERS,
  SOCIAL_SUMMARY,
} from "../data/projects";
import { Lightbox } from "../components/Lightbox";
import { Images } from "lucide-react";

/**
 * Editorial Projects index — 2-column grid of cover cards with category filtering.
 * Special handling for "social-media": shows a category summary + image-only grid.
 */
export default function Projects() {
  const [active, setActive] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const projects = useMemo(() => getProjectsByCategory(active), [active]);
  const showSocial = active === "all" || active === "social-media";
  const onlySocial = active === "social-media";

  const socialImages = SOCIAL_POSTERS.map((p) => p.image);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(-1);
  const prev = () =>
    setLightboxIndex(
      (i) => (i - 1 + socialImages.length) % socialImages.length
    );
  const next = () =>
    setLightboxIndex((i) => (i + 1) % socialImages.length);

  return (
    <section
      className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-16"
      data-testid="projects-page"
    >
      {/* Page intro */}
      <header className="mb-10 md:mb-14 text-center">
        <span className="block text-white/70 font-body font-semibold tracking-[0.4em] uppercase text-xs">
          Selected Work
        </span>
        <h1
          className="mt-4 font-body font-extrabold text-white tracking-tight text-5xl md:text-7xl leading-[1]"
          data-testid="projects-title"
        >
          Projects
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-white/75 font-body font-medium text-base md:text-lg leading-relaxed">
          A curated portfolio across photography, branding, User Interface, and
          social media. Each piece treated as its own editorial story.
        </p>
      </header>

      {/* Category filter */}
      <div
        className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10 md:mb-14"
        data-testid="category-filters"
      >
        {CATEGORIES.map((c) => {
          const isActive = c.id === active;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.id)}
              data-testid={`category-filter-${c.id}`}
              className={[
                "px-4 md:px-5 py-2 rounded-full font-body font-semibold text-xs md:text-sm tracking-wide transition-all duration-300",
                isActive
                  ? "bg-white text-black shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
                  : "bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm",
              ].join(" ")}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Standard project grid (skip when only viewing social-media) */}
      {!onlySocial && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {projects.map((p) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              data-testid={`project-card-${p.slug}`}
              className="group block"
            >
              <article className="bg-[#FEFBEA] rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.45)] transition-transform duration-500 ease-out group-hover:-translate-y-1">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                    draggable={false}
                  />
                  {p.images.length > 1 && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/65 text-white backdrop-blur-sm text-[11px] font-body font-semibold tracking-wide">
                      <Images className="w-3.5 h-3.5" />+{p.images.length} images
                    </div>
                  )}
                </div>
                <div className="px-5 md:px-7 py-5 md:py-6">
                  <div className="flex items-center justify-between gap-4 text-black/45 font-body font-semibold tracking-[0.18em] uppercase text-[10px] md:text-[11px]">
                    <span>
                      {CATEGORIES.find((c) => c.id === p.category)?.label || p.category}
                    </span>
                    <span>{p.year}</span>
                  </div>
                  <h3 className="mt-2 font-body font-extrabold text-black tracking-tight text-xl md:text-2xl leading-tight">
                    {p.title}
                  </h3>
                  {p.preview && (
                    <p className="mt-2 font-body font-medium text-black/65 text-sm md:text-base leading-relaxed line-clamp-2">
                      {p.preview}
                    </p>
                  )}
                </div>
              </article>
            </Link>
          ))}
        </div>
      )}

      {/* Empty state for filters with no items (e.g., UX/UI placeholder) */}
      {!onlySocial && projects.length === 0 && (
        <p
          className="text-center font-body text-white/70 text-lg mt-10"
          data-testid="empty-projects"
        >
          New work in this category coming soon.
        </p>
      )}

      {/* Social Media section */}
      {showSocial && SOCIAL_POSTERS.length > 0 && (
        <div className={onlySocial ? "" : "mt-20 md:mt-28"}>
          <header className="mb-8 md:mb-12 text-center max-w-3xl mx-auto">
            <span className="block text-white/55 font-body font-semibold tracking-[0.22em] uppercase text-[11px]">
              Social Media
            </span>
            <p
              className="mt-5 font-body font-medium text-white/85 text-base md:text-lg leading-[1.8]"
              data-testid="social-summary"
            >
              {SOCIAL_SUMMARY}
            </p>
          </header>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
            data-testid="social-grid"
          >
            {SOCIAL_POSTERS.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => openLightbox(i)}
                data-testid={`social-poster-${p.id}`}
                className="group block w-full aspect-square rounded-2xl overflow-hidden bg-white/5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.45)] transition-transform duration-500 ease-out hover:-translate-y-1"
                aria-label={`Open ${p.alt} fullscreen`}
              >
                <img
                  src={p.image}
                  alt={p.alt}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  loading="lazy"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightboxIndex >= 0 && (
        <Lightbox
          images={socialImages}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={next}
        />
      )}
    </section>
  );
}
